import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Filter, Calendar, Mail, Phone, User, MessageSquare, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, LogOut } from 'lucide-react';
import { exportToCSV } from '../utils/exportToCSV';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
// Mock data type
type Submission = {
  id: string;
  date: string;
  name: string;
  email: string;
  phone: string;
  type: 'צור קשר' | 'הרשמה לפסטיבל' | 'הצטרפות למועדון';
  message: string;
  status: 'חדש' | 'בטיפול' | 'טופל';
};
// Mock data
const MOCK_DATA: Submission[] = [{
  id: '1',
  date: '2023-10-25 14:30',
  name: 'ישראל ישראלי',
  email: 'israel@example.com',
  phone: '050-1234567',
  type: 'צור קשר',
  message: 'אשמח לקבל פרטים נוספים על שיעורי סאפ למתחילים',
  status: 'חדש'
}, {
  id: '2',
  date: '2023-10-24 09:15',
  name: 'דנה כהן',
  email: 'dana@example.com',
  phone: '052-7654321',
  type: 'הרשמה לפסטיבל',
  message: 'מעוניינת להירשם למקצה העממי',
  status: 'בטיפול'
}, {
  id: '3',
  date: '2023-10-23 16:45',
  name: 'רון לוי',
  email: 'ron@example.com',
  phone: '054-9876543',
  type: 'הצטרפות למועדון',
  message: 'האם יש הנחה לתושבי האזור?',
  status: 'טופל'
}, {
  id: '4',
  date: '2023-10-22 11:20',
  name: 'מיכל אברהם',
  email: 'michal@example.com',
  phone: '053-1122334',
  type: 'צור קשר',
  message: 'רוצה לתאם אירוע חברה לקבוצה של 20 איש',
  status: 'חדש'
}, {
  id: '5',
  date: '2023-10-21 13:10',
  name: 'יוסי מזרחי',
  email: 'yossi@example.com',
  phone: '050-5555555',
  type: 'הרשמה לפסטיבל',
  message: '',
  status: 'טופל'
}];
export function AdminDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Submission;
    direction: 'ascending' | 'descending';
  } | null>(null);
  // בדיקת הרשאות בטעינת הדף
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('adminAuthenticated');
    if (isAuthenticated !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    navigate('/admin/login');
  };
  // Filter and sort logic
  const filteredData = useMemo(() => {
    let data = [...MOCK_DATA];
    // Search filter
    if (searchTerm) {
      data = data.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.phone.includes(searchTerm) || item.type.includes(searchTerm));
    }
    // Status filter
    if (statusFilter !== 'all') {
      data = data.filter(item => item.status === statusFilter);
    }
    // Sorting
    if (sortConfig) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [searchTerm, statusFilter, sortConfig]);
  const requestSort = (key: keyof Submission) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleExport = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    exportToCSV(filteredData, `submissions_export_${timestamp}.csv`);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'חדש':
        return 'bg-blue-100 text-blue-800';
      case 'בטיפול':
        return 'bg-yellow-100 text-yellow-800';
      case 'טופל':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'חדש':
        return <Clock className="w-4 h-4 ml-1" data-id="element-1022" data-mp="1ttteueb6afx0" />;
      case 'בטיפול':
        return <Clock className="w-4 h-4 ml-1" data-id="element-1023" data-mp="1fvcz5xvc1f2q" />;
      case 'טופל':
        return <CheckCircle className="w-4 h-4 ml-1" data-id="element-1024" data-mp="1kupu75wcxsge" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" dir="rtl" data-id="element-1025" data-mp="165tfipzsph6t">
      <Navigation data-id="element-1026" data-mp="1ax1pbn51f4ki" />

      <main className="flex-grow container mx-auto px-4 py-8 mt-20" data-id="element-1027" data-mp="1axl979e90316">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1028" data-mp="1ayw7itoxcikw">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white" data-id="element-1029" data-mp="116qk7x9mapc1">
            <div data-id="element-1030" data-mp="n7z7ofaelbq0">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-1031" data-mp="n9k2m60mqwoe">ניהול פניות</h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-1032" data-mp="s60t36vaxn4n">
                צפייה וניהול של כל הטפסים שנשלחו מהאתר
              </p>
            </div>
            <div className="flex gap-3" data-id="element-1033" data-mp="3lxcr4b7sj5f">
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium" data-id="element-1034" data-mp="3myzw75i8w3i">
                <LogOut className="w-4 h-4" data-id="element-1035" data-mp="ditnzhshzjdo" />
                התנתק
              </button>
              <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm" data-id="element-1036" data-mp="riuqf1oeuyv9">
                <Download className="w-4 h-4" data-id="element-1037" data-mp="119z6k4ptopro" />
                ייצוא לאקסל
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row gap-4" data-id="element-1038" data-mp="wcfosutx1uuz">
            <div className="relative flex-grow max-w-md" data-id="element-1039" data-mp="1a818cmhdrmg0">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-1040" data-mp="169k6oigqb45z">
                <Search className="h-5 w-5 text-gray-400" data-id="element-1041" data-mp="1dd1q8kyaojbw" />
              </div>
              <input type="text" className="block w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="חיפוש לפי שם, אימייל או טלפון..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1042" data-mp="1ia0l55ps8ub5" />
            </div>

            <div className="flex items-center gap-2" data-id="element-1043" data-mp="210rz6kkpt8vv">
              <Filter className="w-5 h-5 text-gray-500" data-id="element-1044" data-mp="213e92qv2w0h9" />
              <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-1045" data-mp="261fqz8grptr5">
                <option value="all" data-id="element-1046" data-mp="bog6p21g0bdj">כל הסטטוסים</option>
                <option value="חדש" data-id="element-1047" data-mp="gmhnthfb60md">חדש</option>
                <option value="בטיפול" data-id="element-1048" data-mp="gnay2sdh7899">בטיפול</option>
                <option value="טופל" data-id="element-1049" data-mp="2twrfpe4qxryj">טופל</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-1050" data-mp="2p3q0f50mnqef">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1051" data-mp="2l2way77je3jf">
              <thead className="bg-gray-50" data-id="element-1052" data-mp="2l11ogogma3d8">
                <tr data-id="element-1053" data-mp="2g54b8r0nhd4o">
                  {[{
                  key: 'date',
                  label: 'תאריך',
                  icon: Calendar
                }, {
                  key: 'name',
                  label: 'שם מלא',
                  icon: User
                }, {
                  key: 'phone',
                  label: 'טלפון',
                  icon: Phone
                }, {
                  key: 'email',
                  label: 'אימייל',
                  icon: Mail
                }, {
                  key: 'type',
                  label: 'סוג פנייה',
                  icon: MessageSquare
                }, {
                  key: 'status',
                  label: 'סטטוס',
                  icon: CheckCircle
                }, {
                  key: 'message',
                  label: 'הודעה',
                  icon: MessageSquare
                }].map(column => <th key={column.key} scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort(column.key as keyof Submission)} data-id="element-1054" data-mp="3dk8wimmpoj74">
                      <div className="flex items-center gap-1" data-id="element-1055" data-mp="a53ibpm27azl">
                        <column.icon className="w-3 h-3" data-id="element-1056" data-mp="f0sah7vr1plv" />
                        {column.label}
                        {sortConfig?.key === column.key && (sortConfig.direction === 'ascending' ? <ChevronUp className="w-3 h-3" data-id="element-1057" data-mp="5bs7lde4ma2e" /> : <ChevronDown className="w-3 h-3" data-id="element-1058" data-mp="5apjfd86ulq6" />)}
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1059" data-mp="xsbdvftor5of">
                {filteredData.length > 0 ? filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-1060" data-mp="2ii8cnw2evv5i">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1061" data-mp="2dkf8f8ko5b7v">
                        {row.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1062" data-mp="2s809kwxzh0d5">
                        <div className="text-sm font-medium text-gray-900" data-id="element-1063" data-mp="2nars6yknt0cy">
                          {row.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1064" data-mp="1ulb3aaw901lh">
                        <a href={`tel:${row.phone}`} className="hover:text-blue-600" data-id="element-1065" data-mp="28kuan1h8j8wq">
                          {row.phone}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1066" data-mp="24lbqi7x8n1uy">
                        <a href={`mailto:${row.email}`} className="hover:text-blue-600" data-id="element-1067" data-mp="3jy672rao0p8v">
                          {row.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1068" data-mp="39poxg764s705">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1069" data-mp="35qg4v5mtuf66">
                          {row.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1070" data-mp="2vxhblagdy0g8">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.status)}`} data-id="element-1071" data-mp="2r1a61irwiqyt">
                          {getStatusIcon(row.status)}
                          {row.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={row.message} data-id="element-1072" data-mp="2d210re58hh9m">
                        {row.message || '-'}
                      </td>
                    </tr>) : <tr data-id="element-1073" data-mp="bae692m4v48e">
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1074" data-mp="b9vzvd5rb9mr">
                      <div className="flex flex-col items-center justify-center" data-id="element-1075" data-mp="is2jx2wl9dy0">
                        <XCircle className="w-12 h-12 text-gray-300 mb-3" data-id="element-1076" data-mp="nr6spsflp9nt" />
                        <p className="text-lg font-medium" data-id="element-1077" data-mp="six9oh91mvo7">לא נמצאו תוצאות</p>
                        <p className="text-sm" data-id="element-1078" data-mp="sl1a2p7qhcjq">נסה לשנות את סינון החיפוש</p>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Footer of table */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-1079" data-mp="4sb0gmnaz5cm">
            <div className="text-sm text-gray-500" data-id="element-1080" data-mp="9r4dyf2lawcs">
              מציג <span className="font-medium" data-id="element-1081" data-mp="dtsoqsav6dce">{filteredData.length}</span>{' '}
              תוצאות
            </div>
            <div className="flex gap-2" data-id="element-1082" data-mp="1l7ma0e29ywyw">
              <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled data-id="element-1083" data-mp="1l6t239rky1f6">
                הקודם
              </button>
              <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled data-id="element-1084" data-mp="3hahc88c5za4a">
                הבא
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-1085" data-mp="32lktnhcheepf" />
    </div>;
}